import React from 'react';
import DatePicker from 'react-datepicker';
import type { DatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Label } from '@radix-ui/react-label';
import { cn } from '@utils/utils';

export interface DateTimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  error?: string;
  className?: string;
  minDate?: Date;
  minTime?: Date;
  maxTime?: Date;
  maxDate?: Date;
  placeholder?: string;
  showTimeSelect?: boolean;
  customDisplayFormat?: boolean;
  popperPlacement?: DatePickerProps['popperPlacement'];
}

const parseDateTime = (dateTimeString?: string): Date | null => {
  if (!dateTimeString) return null;
  
  // Si contiene espacio, es formato con tiempo
  if (dateTimeString.includes(" ")) {
    const date = new Date(dateTimeString.replace(" ", "T"));
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Si es solo fecha (yyyy-MM-dd), crear la fecha en zona horaria local
  const [year, month, day] = dateTimeString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month es 0-indexado
  return isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (date: Date | null, showTimeSelect: boolean = true): string => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  if (!showTimeSelect) {
    return `${year}-${month}-${day}`;
  }
  
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};if (typeof document !== 'undefined') {
  let portalDiv = document.getElementById('datepicker-portal');
  if (!portalDiv) {
    portalDiv = document.createElement('div');
    portalDiv.id = 'datepicker-portal';
    document.body.appendChild(portalDiv);
  }
}

const DateTimePicker = React.forwardRef<HTMLDivElement, DateTimePickerProps>(
  ({ value, onChange, disabled, label, error, className, minDate, minTime, maxTime, maxDate, placeholder, showTimeSelect = true, customDisplayFormat = false, popperPlacement = 'bottom-end' }, ref) => {
    const selected = parseDateTime(value);

    const getRelativeDisplayValue = (val?: string): string => {
      if (!val) return "";
      const date = parseDateTime(val);
      if (!date) return val;

      const today = new Date();
      const d1 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const d2 = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      const diffTime = d2.getTime() - d1.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const timeStr = showTimeSelect ? ` ${hours}:${minutes}:${seconds}` : "";

      if (diffDays === 0) {
        return `HOY${timeStr}`;
      }
      if (diffDays === 1) {
        return `MAÑANA${timeStr}`;
      }
      if (diffDays === 2) {
        return `EN 2 DÍAS${timeStr}`;
      }
      return val;
    };

    const displayValue = customDisplayFormat ? getRelativeDisplayValue(value) : undefined;

    return (
      <div className={cn("space-y-2 w-full", className)} ref={ref}>
        {label && (
          <Label
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
          </Label>
        )}
        <DatePicker
          selected={selected}
          value={displayValue}
          onChange={(date: Date | null) => onChange(formatDateTime(date, showTimeSelect))}
          showTimeSelect={showTimeSelect}
          timeFormat="HH:mm:ss"
          dateFormat={showTimeSelect ? "yyyy-MM-dd HH:mm:ss" : "yyyy-MM-dd"}
          timeIntervals={15}
          placeholderText={placeholder || (showTimeSelect ? "Selecciona fecha y hora" : "Selecciona fecha")}
          className={cn(
            "w-full border rounded px-3 py-2 text-left bg-white",
            error ? "border-red-500" : "border-gray-300"
          )}
          disabled={disabled}
          autoComplete="off"
          minDate={minDate}
          maxDate={maxDate}
          minTime={minTime}
          maxTime={maxTime}
          popperPlacement={popperPlacement}
          portalId="datepicker-portal"
        />
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      </div>
    );
  }
);
DateTimePicker.displayName = "DateTimePicker";

export { DateTimePicker }; 